import { PRESENCE_ONLINE_MINUTEN, type Presence } from '@fh/shared'

import { usePrefs } from '../prefs.js'
import { t } from './t.js'

/**
 * Anwesenheit und Zeitangaben in beiden Sprachen.
 *
 * ## Warum das hier steht und nicht in `@fh/shared`
 *
 * Dort stehen dieselben Funktionen — aber `@fh/shared` benutzt auch der
 * Server, und der hat keine eingestellte Sprache. Er weiß nicht, wer die
 * Antwort liest; die Übersetzung gehört deshalb dorthin, wo die Einstellung
 * liegt.
 *
 * Die Logik ist bewusst Wort für Wort dieselbe wie in `shared/status.ts`.
 * Damit sie es bleibt, vergleicht `zeit.test.ts` beide bei deutscher
 * Einstellung an denselben Zeitpunkten. Zwei Kopien derselben Regel driften
 * sonst auseinander, und zwar an der Stelle, an der es niemand nachrechnet.
 *
 * ## Zur Übersetzung selbst
 *
 * „Vor 20 Minuten" statt „Zuletzt online: 14:32" — eine Uhrzeit kann niemand
 * einordnen, der nicht weiß, wie spät es gerade ist. Im Englischen gilt
 * dasselbe, nur andersherum gebaut: „20 min ago".
 */

export function anwesenheit(
  presence: Presence,
  lastSeenAt: string | null,
  jetzt = Date.now(),
): string {
  if (presence === 'online') return t('Jetzt online')
  if (!lastSeenAt) return t('Noch nie angemeldet')

  const minuten = Math.floor((jetzt - new Date(lastSeenAt).getTime()) / 60_000)
  if (minuten < 1) return t('Gerade eben')
  if (minuten < 60) return t('Vor {n} Min.', { n: minuten })

  const stunden = Math.floor(minuten / 60)
  if (stunden < 24) return t('Vor {n} Std.', { n: stunden })

  const tage = Math.floor(stunden / 24)
  if (tage === 1) return t('Gestern')
  if (tage < 7) return t('Vor {n} Tagen', { n: tage })
  return t('Länger nicht da')
}

/** Dieselbe Sprache, aber kleingeschrieben — sie steht mitten im Satz. */
export function wann(zeitpunkt: string, jetzt = Date.now()): string {
  const minuten = Math.floor((jetzt - new Date(zeitpunkt).getTime()) / 60_000)
  if (minuten < 1) return t('gerade eben')
  if (minuten < 60) return t('vor {n} Min.', { n: minuten })

  const stunden = Math.floor(minuten / 60)
  if (stunden < 24) return t('vor {n} Std.', { n: stunden })

  // Kein „vor längerer Zeit" wie bei der Anwesenheit: `wannText` in
  // `@fh/shared` zählt die Tage unbegrenzt weiter, und die beiden Fassungen
  // müssen gleich rechnen. Geprüft in `zeit.test.ts`.
  const tage = Math.floor(stunden / 24)
  return tage === 1 ? t('gestern') : t('vor {n} Tagen', { n: tage })
}

/**
 * Der Zeitstempel rechts in einer Listenzeile — wie in jedem Messenger.
 *
 * Heute die Uhrzeit („18:38"), gestern das Wort, davor das Datum. Nicht
 * relativ („vor 3 Tagen"): In einer Liste, in der jede Zeile eine Zeit trägt,
 * sind relative Angaben zwar freundlicher zu lesen, aber unbrauchbar zum
 * Vergleichen — „vor 2 Std." über „vor 90 Min." liest sich wie eine falsche
 * Sortierung, obwohl sie stimmt.
 *
 * Hier stand vorher schlicht `toLocaleTimeString`. Eine Nachricht von letzter
 * Woche zeigte damit eine Uhrzeit ohne Tag — die Zeile behauptete, sie sei von
 * heute.
 */
export function listenZeit(zeitpunkt: string, jetzt = Date.now()): string {
  const dann = new Date(zeitpunkt)
  const heute = new Date(jetzt)

  const gleicherTag = dann.toDateString() === heute.toDateString()
  if (gleicherTag) {
    return dann.toLocaleTimeString(datumsSprache(), { hour: '2-digit', minute: '2-digit' })
  }

  const gestern = new Date(jetzt)
  gestern.setDate(gestern.getDate() - 1)
  if (dann.toDateString() === gestern.toDateString()) return t('Gestern')

  return dann.toLocaleDateString(datumsSprache(), {
    day: '2-digit', month: '2-digit', year: '2-digit',
  })
}

export { PRESENCE_ONLINE_MINUTEN }

/**
 * Die Locale für `toLocaleDateString`.
 *
 * Überall im Code stand `'de-DE'` fest. Ein englischer Nutzer bekäme damit
 * „01.05.2026" statt „5/1/2026" — dieselbe Zahl, andere Bedeutung: Wer
 * amerikanische Daten gewohnt ist, liest den ersten Wert als Monat.
 */
export function datumsSprache(): string {
  return usePrefs.getState().sprache === 'en' ? 'en-GB' : 'de-DE'
}

/**
 * Gesprächsdauer in Alltagssprache — „3 Min. 20 Sek." statt „00:03:20".
 *
 * Dieselbe Rechnung wie `dauerText` in `@fh/shared`, aus demselben Grund wie
 * oben noch einmal hier: Der Server kennt keine eingestellte Sprache.
 */
export function dauer(sekunden: number): string {
  if (sekunden < 60) return t('{n} Sek.', { n: sekunden })
  const min = Math.floor(sekunden / 60)
  const rest = sekunden % 60
  return rest === 0
    ? t('{n} Min.', { n: min })
    : t('{min} Min. {sek} Sek.', { min, sek: rest })
}
